import React, { useState, useEffect } from 'react';
import { Users, Loader2, Calendar, Globe, Monitor, CheckCircle2, X, Eye, MousePointerClick } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { getAuthHeaders, checkTokenExpiration } from '../lib/auth';
import { toast } from './ui/sonner';

const EmailVisits = ({ sentEmailId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [emailData, setEmailData] = useState(null);
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState({
    totalVisits: 0,
    totalConversions: 0,
    conversionRate: 0,
  });

  useEffect(() => {
    if (sentEmailId) {
      fetchEmailVisits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentEmailId]);

  const fetchEmailVisits = async () => {
    setLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/campaigns/email/${sentEmailId}/visits`, {
        headers: getAuthHeaders(),
      });

      if (checkTokenExpiration(response)) {
        return;
      }

      const data = await response.json();

      if (data.success) {
        setEmailData(data.email);
        setVisits(data.visits);
        setStats({
          totalVisits: data.totalVisits,
          totalConversions: data.totalConversions,
          conversionRate: data.totalVisits > 0
            ? ((data.totalConversions / data.totalVisits) * 100).toFixed(2)
            : '0.00',
        });
      } else {
        toast.error('Failed to load email visits', {
          description: data.error || 'Please try again later.',
        });
      }
    } catch (error) {
      console.error('Error fetching email visits:', error);
      toast.error('Failed to load email visits');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getDeviceInfo = (userAgent) => {
    if (!userAgent || userAgent === 'unknown') return 'Unknown';
    
    if (userAgent.includes('Mobile')) {
      return 'Mobile';
    } else if (userAgent.includes('Tablet')) {
      return 'Tablet';
    } else {
      return 'Desktop';
    }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-[#d9fb06] mx-auto" />
        </div>
      ) : (
        <>
          {/* Email Info */}
          {emailData && (
            <Card className="border-[#3f4816] bg-[#1a1c1b]">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-[#888680] mb-1">Recipient</p>
                    <p className="text-white font-medium">{emailData.recipient}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#888680] mb-1">Subject</p>
                    <p className="text-white font-medium truncate">{emailData.subject}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#888680] mb-1">Sent At</p>
                    <p className="text-white font-medium">{formatDate(emailData.sentAt)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#888680]">Opens</p>
                    <p className="text-2xl font-bold text-[#d9fb06] mt-1">{emailData?.openCount || 0}</p>
                  </div>
                  <Eye className="h-8 w-8 text-[#d9fb06] opacity-50" />
                </div>
                {emailData?.lastOpenedAt && (
                  <p className="text-xs text-[#888680] mt-2">
                    Last: {formatDate(emailData.lastOpenedAt)}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#888680]">Clicks</p>
                    <p className="text-2xl font-bold text-[#d9fb06] mt-1">{emailData?.clickCount || 0}</p>
                  </div>
                  <MousePointerClick className="h-8 w-8 text-[#d9fb06] opacity-50" />
                </div>
                {emailData?.lastClickedAt && (
                  <p className="text-xs text-[#888680] mt-2">
                    Last: {formatDate(emailData.lastClickedAt)}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#888680]">Total Visits</p>
                    <p className="text-2xl font-bold text-[#d9fb06] mt-1">{stats.totalVisits}</p>
                  </div>
                  <Users className="h-8 w-8 text-[#d9fb06] opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#888680]">Conversions</p>
                    <p className="text-2xl font-bold text-[#d9fb06] mt-1">{stats.totalConversions}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-[#d9fb06] opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#888680]">Conversion Rate</p>
                    <p className="text-2xl font-bold text-[#d9fb06] mt-1">{stats.conversionRate}%</p>
                  </div>
                  <Globe className="h-8 w-8 text-[#d9fb06] opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Visits Table */}
          <Card className="border-[#3f4816] bg-[#302f2c]">
            <CardHeader>
              <CardTitle className="text-[#d9fb06]">Visit Details</CardTitle>
            </CardHeader>
            <CardContent>
              {visits.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-[#888680] mx-auto mb-3" />
                  <p className="text-[#888680]">No visits recorded for this email</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#3f4816]">
                        <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Date</th>
                        <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">IP Address</th>
                        <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Device</th>
                        <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">User Agent</th>
                        <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Referer</th>
                        <th className="text-center py-3 px-4 text-[#d9fb06] font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visits.map((visit) => (
                        <tr key={visit.id} className="border-b border-[#3f4816] hover:bg-[#1a1c1b]">
                          <td className="py-3 px-4 text-white text-sm">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3 text-[#888680]" />
                              <span>{formatDate(visit.visitedAt)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[#888680] text-sm">
                            {visit.ipAddress || 'Unknown'}
                          </td>
                          <td className="py-3 px-4 text-[#888680] text-sm">
                            <div className="flex items-center space-x-1">
                              <Monitor className="h-3 w-3" />
                              <span>{getDeviceInfo(visit.userAgent)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[#888680] text-sm max-w-xs truncate" title={visit.userAgent}>
                            {visit.userAgent || 'Unknown'}
                          </td>
                          <td className="py-3 px-4 text-[#888680] text-sm max-w-xs truncate" title={visit.referer}>
                            {visit.referer || 'Direct'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {visit.converted ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Converted
                              </Badge>
                            ) : (
                              <Badge className="bg-[#3f4816] text-[#888680] border-[#3f4816]">
                                Pending
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default EmailVisits;
